import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowPathIcon,
  CloudArrowDownIcon,
  CloudArrowUpIcon,
  MagnifyingGlassIcon,
  PlusIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { Eye, History, Pencil, RotateCcw, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';
import ConfirmDialog from '../MasterAdmin/ui/ConfirmDialog';
import WhatsAppPanelShell from './WhatsAppPanelShell';
import WhatsAppDevicePreview from './WhatsAppDevicePreview';
import WhatsAppTemplateCreateWizard from './WhatsAppTemplateCreateWizard';
import {
  devicePreviewFromTemplate,
  templateButtonTypeLabel,
} from './whatsappTemplatePreview';
import { getApiErrorMessage } from '../../utils/apiErrorMessage';
import type {
  WhatsAppTemplate,
  WhatsAppTemplateDeletionLog,
  WhatsAppTemplateSyncStatus,
  WhatsAppTemplateVersionHistoryEntry,
} from '../../models/MasterAdmin/whatsappCampaign';
import {
  createWhatsAppTemplate,
  deleteWhatsAppTemplate,
  fetchWhatsAppTemplateDeletionLogs,
  fetchWhatsAppTemplateList,
  fetchWhatsAppTemplateVersions,
  patchWhatsAppTemplateVariableMappings,
  restoreWhatsAppTemplateFromLog,
  submitWhatsAppTemplateToInterakt,
  syncInteraktWhatsAppTemplates,
  syncWhatsAppTemplateToDatabase,
  updateWhatsAppTemplate,
  type WhatsAppTemplateListOverview,
} from '../../services/MasterAdmin/whatsappHubService';
import WhatsAppTemplateMappingsSection, {
  validateTemplateMappings,
} from './WhatsAppTemplateMappingsSection';
import type { WhatsAppTemplateVariableMapping } from '../../models/MasterAdmin/whatsappHub';
import {
  buildTemplatePayload,
  emptyForm,
  templateToForm,
  validateTemplateForm,
  type TemplateFormState,
} from './whatsappTemplateForm';
import { fetchDeepLinkCampaigns } from '../../services/MasterAdmin/deepLinkService';
import type { DeepLinkCampaign } from '../../models/MasterAdmin/deepLink';

function ApprovalBadge({ status }: { status?: string | null }) {
  if (!status) return <span className="text-xs text-gray-400">—</span>;
  const normalized = status.toUpperCase();
  const cls =
    normalized === 'APPROVED'
      ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
      : normalized === 'PENDING'
        ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300'
        : normalized === 'DRAFT'
          ? 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300'
          : normalized === 'REJECTED'
            ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
            : normalized === 'DELETED_LOCALLY'
              ? 'bg-gray-200 text-gray-700 dark:bg-gray-800 dark:text-gray-300'
              : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400';
  return (
    <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${cls}`}>
      {normalized}
    </span>
  );
}

function SyncStatusBadge({
  inDatabase,
  status,
}: {
  inDatabase?: boolean | null;
  status?: WhatsAppTemplateSyncStatus | null;
}) {
  const saved =
    typeof inDatabase === 'boolean'
      ? inDatabase
      : status === 'SYNCED' || status === 'DB_ONLY';

  if (saved) {
    return (
      <span
        className="inline-flex rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300"
        title="Template is saved in Vedika database"
      >
        Saved
      </span>
    );
  }

  return (
    <span
      className="inline-flex rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800 dark:bg-amber-900/30 dark:text-amber-300"
      title="Template exists on Interakt but is not saved in Vedika database yet — use Sync to save it"
    >
      Not saved
    </span>
  );
}

const actionBtnClass =
  'rounded p-1.5 text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800';

/** Stable key for template API routes (Interakt code name preferred). */
function templateApiKey(t: WhatsAppTemplate): string {
  return t.name || t.interaktTemplateId || t.localTemplateId || t.id;
}

const PAGE_SIZE = 20;

const emptyOverview = (): WhatsAppTemplateListOverview => ({
  total: 0,
  approved: 0,
  pending: 0,
  rejected: 0,
  draft: 0,
  synced: 0,
  interaktOnly: 0,
  dbOnly: 0,
});

const WhatsAppTemplatesPanel: React.FC = () => {
  const [templates, setTemplates] = useState<WhatsAppTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [offset, setOffset] = useState(0);
  const [filteredTotal, setFilteredTotal] = useState(0);
  const [overview, setOverview] = useState<WhatsAppTemplateListOverview>(emptyOverview);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [approvalStatus, setApprovalStatus] = useState('ALL');
  const [inDatabaseFilter, setInDatabaseFilter] = useState('ALL');
  const [category, setCategory] = useState('ALL');
  const [languageCode, setLanguageCode] = useState('all');
  const [headerFormat, setHeaderFormat] = useState('ALL');
  const [saving, setSaving] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [editTemplate, setEditTemplate] = useState<WhatsAppTemplate | null>(null);
  const [viewTemplate, setViewTemplate] = useState<WhatsAppTemplate | null>(null);
  const [form, setForm] = useState<TemplateFormState>(emptyForm());
  const [confirmEdit, setConfirmEdit] = useState<WhatsAppTemplate | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<WhatsAppTemplate | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [mappingSaving, setMappingSaving] = useState(false);
  const [viewMappings, setViewMappings] = useState<WhatsAppTemplateVariableMapping[]>([]);
  const [syncingAll, setSyncingAll] = useState(false);
  const [syncingId, setSyncingId] = useState<string | null>(null);
  const [showDeletedLogs, setShowDeletedLogs] = useState(false);
  const [deletionLogs, setDeletionLogs] = useState<WhatsAppTemplateDeletionLog[]>([]);
  const [loadingDeletionLogs, setLoadingDeletionLogs] = useState(false);
  const [restoringLogId, setRestoringLogId] = useState<string | null>(null);
  const [versionHistory, setVersionHistory] = useState<WhatsAppTemplateVersionHistoryEntry[]>([]);
  const [loadingVersions, setLoadingVersions] = useState(false);
  const [submittingId, setSubmittingId] = useState<string | null>(null);
  const [deepLinkCampaigns, setDeepLinkCampaigns] = useState<DeepLinkCampaign[]>([]);
  const loadMoreRef = useRef<HTMLDivElement | null>(null);
  const fetchingMoreRef = useRef(false);

  const campaignsById = useMemo(
    () => new Map(deepLinkCampaigns.map((c) => [c.id, c])),
    [deepLinkCampaigns]
  );

  useEffect(() => {
    fetchDeepLinkCampaigns()
      .then((rows) => setDeepLinkCampaigns(rows || []))
      .catch(() => setDeepLinkCampaigns([]));
  }, []);

  const viewPreviewProps = useMemo(
    () => (viewTemplate ? devicePreviewFromTemplate(viewTemplate) : null),
    [viewTemplate]
  );

  const loadPage = useCallback(
    async (nextOffset: number, append: boolean, forceRefresh = false) => {
      if (append) {
        if (fetchingMoreRef.current) return;
        fetchingMoreRef.current = true;
        setLoadingMore(true);
      } else {
        setLoading(true);
      }
      try {
        const res = await fetchWhatsAppTemplateList({
          allStatuses: true,
          paginated: true,
          search: search || undefined,
          approvalStatus,
          inDatabase:
            inDatabaseFilter === 'YES' ? true : inDatabaseFilter === 'NO' ? false : undefined,
          category,
          languageCode,
          headerFormat,
          limit: PAGE_SIZE,
          offset: nextOffset,
          refresh: forceRefresh,
        });
        const rows = res.data || [];
        setTemplates((prev) => (append ? [...prev, ...rows] : rows));
        setOffset(nextOffset + rows.length);
        setHasMore(Boolean(res.meta?.hasMore));
        setFilteredTotal(Number(res.meta?.total) || rows.length);
        if (res.meta?.overview) setOverview(res.meta.overview);
      } catch (err: unknown) {
        toast.error(getApiErrorMessage(err, 'Failed to load templates'));
      } finally {
        if (append) {
          fetchingMoreRef.current = false;
          setLoadingMore(false);
        } else {
          setLoading(false);
        }
      }
    },
    [search, approvalStatus, inDatabaseFilter, category, languageCode, headerFormat]
  );

  const load = useCallback(
    async (forceRefresh = false) => {
      setOffset(0);
      await loadPage(0, false, forceRefresh);
    },
    [loadPage]
  );

  useEffect(() => {
    const timer = window.setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => window.clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!hasMore || loading || loadingMore) return;
    const node = loadMoreRef.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && hasMore && !loading && !loadingMore) {
          void loadPage(offset, true);
        }
      },
      { root: null, rootMargin: '160px', threshold: 0 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasMore, loading, loadingMore, offset, loadPage]);

  const loadDeletionLogs = useCallback(async () => {
    setLoadingDeletionLogs(true);
    try {
      const res = await fetchWhatsAppTemplateDeletionLogs();
      setDeletionLogs(res.data || []);
    } catch (err: unknown) {
      toast.error(getApiErrorMessage(err, 'Failed to load deletion logs'));
    } finally {
      setLoadingDeletionLogs(false);
    }
  }, []);

  const loadVersionHistory = useCallback(async (template: WhatsAppTemplate) => {
    setLoadingVersions(true);
    setVersionHistory([]);
    try {
      const res = await fetchWhatsAppTemplateVersions(templateApiKey(template));
      setVersionHistory(res.data || []);
    } catch (err: unknown) {
      toast.error(getApiErrorMessage(err, 'Failed to load version history'));
    } finally {
      setLoadingVersions(false);
    }
  }, []);

  const handleSyncAll = async () => {
    setSyncingAll(true);
    try {
      const res = await syncInteraktWhatsAppTemplates({ approvalStatus: 'all' });
      toast.success(res.message || 'Templates synced to database');
      await load(true);
    } catch (err: unknown) {
      toast.error(getApiErrorMessage(err, 'Sync failed'));
    } finally {
      setSyncingAll(false);
    }
  };

  const handleSyncOne = async (t: WhatsAppTemplate) => {
    const key = templateApiKey(t);
    setSyncingId(key);
    try {
      const res = await syncWhatsAppTemplateToDatabase(key);
      toast.success(res.message || 'Template synced to Vedika database');
      await load();
      if (viewTemplate && templateApiKey(viewTemplate) === key) {
        setViewTemplate(res.data?.template || null);
      }
    } catch (err: unknown) {
      toast.error(getApiErrorMessage(err, 'Sync to Vedika DB failed'), { duration: 8000 });
    } finally {
      setSyncingId(null);
    }
  };

  const buildEditPayload = () => {
    const payload = buildTemplatePayload(form, campaignsById);
    return {
      name: payload.name,
      displayName: payload.displayName,
      category: payload.category,
      languageCode: payload.languageCode,
      bodyPreview: payload.bodyPreview,
      variableCount: payload.variableCount,
      defaultVariableMappings: payload.defaultVariableMappings,
      headerFormat: payload.headerFormat,
      headerText: payload.headerText,
      headerHandleFileUrl: payload.headerHandleFileUrl,
      headerHandleFileName: payload.headerHandleFileName,
      footer: payload.footer,
      buttons: payload.buttons,
    };
  };

  const handleSubmitToInterakt = async (t?: WhatsAppTemplate) => {
    const target = t || editTemplate;
    if (!target) return;

    if (editTemplate && templateApiKey(editTemplate) === templateApiKey(target)) {
      const error = validateTemplateForm(form, { isEdit: true, campaignsById });
      if (error) {
        toast.error(error);
        return;
      }
    }

    const key = templateApiKey(target);
    setSubmittingId(key);
    setSaving(true);
    try {
      const payload =
        editTemplate && templateApiKey(editTemplate) === key
          ? buildEditPayload()
          : undefined;
      const res = await submitWhatsAppTemplateToInterakt(key, payload);
      if (res.localOnly) {
        toast(res.message || 'Saved in Vedika DB. Template already exists on Interakt.', {
          icon: '⚠️',
          duration: 7000,
        });
      } else {
        toast.success(res.message || 'Saved in Vedika DB and published to Interakt for Meta approval');
      }
      if (editTemplate && templateApiKey(editTemplate) === key) closeEdit();
      if (viewTemplate && templateApiKey(viewTemplate) === key) {
        setViewTemplate(res.data || null);
      }
      await load();
    } catch (err: unknown) {
      toast.error(getApiErrorMessage(err, 'Failed to submit to Interakt'), { duration: 8000 });
    } finally {
      setSubmittingId(null);
      setSaving(false);
    }
  };

  const handleRestoreFromLog = async (logId: string) => {
    setRestoringLogId(logId);
    try {
      const res = await restoreWhatsAppTemplateFromLog(logId);
      toast.success(res.message || 'Template restored');
      await load();
      await loadDeletionLogs();
    } catch (err: unknown) {
      toast.error(getApiErrorMessage(err, 'Restore failed'));
    } finally {
      setRestoringLogId(null);
    }
  };

  const closeCreate = () => {
    setShowCreate(false);
    setForm(emptyForm());
  };

  const closeEdit = () => {
    setEditTemplate(null);
    setForm(emptyForm());
  };

  const handleDraftCreate = async () => {
    const error = validateTemplateForm(form, { campaignsById });
    if (error) {
      toast.error(error);
      return;
    }
    setSaving(true);
    try {
      const res = await createWhatsAppTemplate({
        ...(buildTemplatePayload(form, campaignsById) as Parameters<typeof createWhatsAppTemplate>[0]),
        draft: true,
      });
      toast.success(res.message || 'Draft saved in Vedika database');
      closeCreate();
      await load();
    } catch (err: unknown) {
      toast.error(getApiErrorMessage(err, 'Failed to save draft'), { duration: 8000 });
    } finally {
      setSaving(false);
    }
  };

  const handlePublishCreate = async () => {
    const error = validateTemplateForm(form, { campaignsById });
    if (error) {
      toast.error(error);
      return;
    }
    setSaving(true);
    try {
      const res = await createWhatsAppTemplate(
        buildTemplatePayload(form, campaignsById) as Parameters<typeof createWhatsAppTemplate>[0]
      );
      if (res.localOnly) {
        toast(res.message || 'Template already exists on Interakt — saved in Vedika DB', {
          icon: '⚠️',
          duration: 6000,
        });
      } else {
        toast.success(res.message || 'Saved in Vedika DB and published to Interakt');
      }
      closeCreate();
      await load();
    } catch (err: unknown) {
      toast.error(getApiErrorMessage(err, 'Failed to publish template'), { duration: 8000 });
    } finally {
      setSaving(false);
    }
  };

  const handleDraft = async () => {
    if (!editTemplate) {
      await handleDraftCreate();
      return;
    }
    await handleSaveLocalOnly();
  };

  const handlePublish = async () => {
    if (!editTemplate) {
      await handlePublishCreate();
      return;
    }

    if (editTemplate.syncStatus === 'INTERAKT_ONLY') {
      setSaving(true);
      try {
        await handleSyncOne(editTemplate);
        closeEdit();
      } finally {
        setSaving(false);
      }
      return;
    }

    await handleSubmitToInterakt(editTemplate);
  };

  const handleSaveLocalOnly = async () => {
    if (!editTemplate) return;
    const error = validateTemplateForm(form, { isEdit: true, campaignsById });
    if (error) {
      toast.error(error);
      return;
    }
    setSaving(true);
    try {
      let apiKey = templateApiKey(editTemplate);
      if (editTemplate.inDatabase === false || editTemplate.syncStatus === 'INTERAKT_ONLY') {
        const synced = await syncWhatsAppTemplateToDatabase(apiKey);
        apiKey = synced.data?.template?.name || apiKey;
      }
      const payload = buildEditPayload();
      const res = await updateWhatsAppTemplate(apiKey, {
        ...payload,
        localOnly: true,
      });
      toast.success(res.message || 'Draft saved in Vedika database');
      closeEdit();
      if (viewTemplate?.id === editTemplate.id) setViewTemplate(null);
      await load();
    } catch (err: unknown) {
      toast.error(getApiErrorMessage(err, 'Failed to save draft'), { duration: 8000 });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!confirmDelete) return;
    setDeleting(true);
    try {
      const res = await deleteWhatsAppTemplate(templateApiKey(confirmDelete));
      toast.success(
        res.message ||
          'Template removed from Vedika database. Deletion log saved — you can restore it later.'
      );
      if (viewTemplate?.id === confirmDelete.id) setViewTemplate(null);
      if (editTemplate?.id === confirmDelete.id) closeEdit();
      setConfirmDelete(null);
      await load();
    } catch (err: unknown) {
      toast.error(getApiErrorMessage(err, 'Failed to delete template'));
    } finally {
      setDeleting(false);
    }
  };

  const handleSaveViewMappings = async () => {
    if (!viewTemplate) return;
    const error = validateTemplateMappings(viewMappings, viewTemplate.bodyPreview || '');
    if (error) {
      toast.error(error);
      return;
    }
    setMappingSaving(true);
    try {
      const res = await patchWhatsAppTemplateVariableMappings(templateApiKey(viewTemplate), {
        defaultVariableMappings: viewMappings,
      });
      toast.success('Variable mappings saved');
      setViewTemplate(res.data);
      await load();
    } catch (err: unknown) {
      toast.error(getApiErrorMessage(err, 'Failed to save mappings'));
    } finally {
      setMappingSaving(false);
    }
  };

  const openViewTemplate = (t: WhatsAppTemplate) => {
    setViewTemplate(t);
    setViewMappings(t.defaultVariableMappings || []);
    void loadVersionHistory(t);
  };

  const startEdit = (t: WhatsAppTemplate) => {
    const template = { ...t };
    setShowCreate(false);
    setViewTemplate(null);
    setConfirmEdit(null);
    setEditTemplate(template);
    setForm(templateToForm(template));
  };

  const renderActionButtons = (t: WhatsAppTemplate) => {
    const key = templateApiKey(t);
    const isInteraktOnly = t.syncStatus === 'INTERAKT_ONLY' || (t.inInterakt && t.inDatabase === false);
    const needsInteraktSubmit =
      t.syncStatus === 'DB_ONLY' ||
      (t.inDatabase !== false && (t.inInterakt === false || t.interaktPresent === false));

    return (
      <div className="flex items-center justify-end gap-0.5">
        {isInteraktOnly && (
          <button
            type="button"
            onClick={() => void handleSyncOne(t)}
            disabled={syncingId === key}
            className={actionBtnClass}
            title="Sync to Vedika database"
            aria-label="Sync to Vedika database"
          >
            <CloudArrowDownIcon
              className={`h-4 w-4 text-sky-600 dark:text-sky-400 ${syncingId === key ? 'animate-pulse' : ''}`}
            />
          </button>
        )}
        {needsInteraktSubmit && (
          <button
            type="button"
            onClick={() => {
              startEdit(t);
            }}
            className={actionBtnClass}
            title="Edit & submit to Interakt"
            aria-label="Edit and submit to Interakt"
          >
            <CloudArrowUpIcon className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
          </button>
        )}
        <button
          type="button"
          onClick={() => openViewTemplate(t)}
          className={actionBtnClass}
          title="View template"
          aria-label="View template"
        >
          <Eye className="h-4 w-4" />
        </button>
        <button
          type="button"
          onClick={() => setConfirmEdit(t)}
          className={actionBtnClass}
          title="Edit template"
          aria-label="Edit template"
        >
          <Pencil className="h-4 w-4" />
        </button>
        {t.inDatabase !== false && (
          <button
            type="button"
            onClick={() => setConfirmDelete(t)}
            className={`${actionBtnClass} text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950/40`}
            title="Delete from database"
            aria-label="Delete from database"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        )}
      </div>
    );
  };

  return (
    <WhatsAppPanelShell
      title="Templates"
      description="Templates from Interakt and Vedika DB. Edit code name locally, sync Interakt-only rows into Vedika, or submit DB-only rows to Interakt. Deleting here only removes from DB."
      actions={
        <>
          <button
            type="button"
            disabled={loading}
            onClick={() => void load(true)}
            className="inline-flex items-center gap-2 rounded-lg border border-gray-200 px-4 py-2 text-sm dark:border-gray-600 disabled:opacity-50"
          >
            <ArrowPathIcon className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <button
            type="button"
            disabled={syncingAll || loading}
            onClick={() => void handleSyncAll()}
            className="inline-flex items-center gap-2 rounded-lg border border-sky-200 bg-sky-50 px-4 py-2 text-sm text-sky-800 hover:bg-sky-100 disabled:opacity-50 dark:border-sky-800 dark:bg-sky-950/40 dark:text-sky-200"
            title="Pull all Interakt templates into Vedika database"
          >
            <CloudArrowDownIcon className={`h-4 w-4 ${syncingAll ? 'animate-pulse' : ''}`} />
            {syncingAll ? 'Syncing...' : 'Sync all from Interakt'}
          </button>
          <button
            type="button"
            onClick={() => {
              setShowDeletedLogs((v) => !v);
              if (!showDeletedLogs) void loadDeletionLogs();
            }}
            className="inline-flex items-center gap-2 rounded-lg border border-gray-200 px-4 py-2 text-sm dark:border-gray-600"
          >
            <History className="h-4 w-4" />
            Deleted logs
          </button>
          <button
            type="button"
            onClick={() => {
              setViewTemplate(null);
              closeEdit();
              setForm(emptyForm());
              setShowCreate(true);
            }}
            className="inline-flex items-center gap-2 rounded-lg bg-[#25D366] px-4 py-2 text-sm font-semibold text-white hover:bg-[#1da851]"
          >
            <PlusIcon className="h-4 w-4" />
            New template
          </button>
        </>
      }
    >
      {showDeletedLogs && (
        <div className="mb-6 rounded-xl border border-gray-200 bg-gray-50/80 p-4 dark:border-gray-700 dark:bg-gray-900/40">
          <div className="flex items-center justify-between gap-2">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Deletion logs (recoverable)</h3>
            <button
              type="button"
              onClick={() => setShowDeletedLogs(false)}
              className="text-xs text-gray-500 hover:underline"
            >
              Hide
            </button>
          </div>
          <p className="mt-1 text-xs text-gray-500">
            Templates deleted manually from Vedika database. Interakt is not affected. Restore to bring them back.
          </p>
          {loadingDeletionLogs ? (
            <p className="mt-3 text-sm text-gray-500">Loading deletion logs...</p>
          ) : deletionLogs.length === 0 ? (
            <p className="mt-3 text-sm text-gray-500">No deletion logs yet.</p>
          ) : (
            <ul className="mt-3 space-y-2">
              {deletionLogs.map((log) => (
                <li
                  key={log.id}
                  className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-900"
                >
                  <div>
                    <p className="font-medium">{log.templateName}</p>
                    <p className="text-xs text-gray-500">
                      Deleted {new Date(log.createdAt).toLocaleString()}
                      {log.restoredAt ? ` · Restored ${new Date(log.restoredAt).toLocaleString()}` : ''}
                    </p>
                  </div>
                  {!log.restoredAt && (
                    <button
                      type="button"
                      disabled={restoringLogId === log.id}
                      onClick={() => void handleRestoreFromLog(log.id)}
                      className="inline-flex items-center gap-1 rounded-lg border border-emerald-200 px-3 py-1.5 text-xs font-medium text-emerald-700 hover:bg-emerald-50 disabled:opacity-50 dark:border-emerald-800 dark:text-emerald-300"
                    >
                      <RotateCcw className="h-3.5 w-3.5" />
                      {restoringLogId === log.id ? 'Restoring...' : 'Restore'}
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {showCreate && (
        <WhatsAppTemplateCreateWizard
          open={showCreate}
          mode="create"
          form={form}
          setForm={setForm}
          onClose={closeCreate}
          onDraft={() => handleDraftCreate()}
          onPublish={() => handlePublishCreate()}
          saving={saving}
        />
      )}

      {editTemplate && (
        <WhatsAppTemplateCreateWizard
          open={Boolean(editTemplate)}
          mode="edit"
          form={form}
          setForm={setForm}
          editTemplate={editTemplate}
          onClose={closeEdit}
          onDraft={() => handleDraft()}
          onPublish={() => handlePublish()}
          saving={saving || submittingId === templateApiKey(editTemplate)}
          showDraftButton={editTemplate.syncStatus !== 'INTERAKT_ONLY'}
          publishLabel={
            editTemplate.syncStatus === 'INTERAKT_ONLY' ? 'Sync to Vedika DB' : 'Publish to Interakt'
          }
        />
      )}

      {viewTemplate && (
        <div className="fixed inset-0 z-[100] flex items-end justify-center bg-black/40 p-0 sm:items-center sm:p-4">
          <div className="flex max-h-[92vh] w-full max-w-5xl flex-col overflow-hidden rounded-t-2xl bg-white shadow-xl sm:rounded-2xl dark:bg-gray-900">
            <div className="flex shrink-0 items-start justify-between gap-3 border-b border-gray-200 px-5 py-4 dark:border-gray-700">
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    {viewTemplate.displayName || viewTemplate.name}
                  </h3>
                  <ApprovalBadge status={viewTemplate.approvalStatus} />
                  <SyncStatusBadge inDatabase={viewTemplate.inDatabase} status={viewTemplate.syncStatus} />
                  {viewTemplate.currentVersion ? (
                    <span className="text-xs text-gray-500">v{viewTemplate.currentVersion}</span>
                  ) : null}
                </div>
                <p className="font-mono text-xs text-gray-500">{viewTemplate.name}</p>
              </div>
              <div className="flex shrink-0 items-center gap-1">
                {viewTemplate.syncStatus === 'INTERAKT_ONLY' && (
                  <button
                    type="button"
                    onClick={() => void handleSyncOne(viewTemplate)}
                    disabled={syncingId === templateApiKey(viewTemplate)}
                    className={actionBtnClass}
                    title="Sync to Vedika database"
                    aria-label="Sync to Vedika database"
                  >
                    <CloudArrowDownIcon
                      className={`h-4 w-4 text-sky-600 ${syncingId === templateApiKey(viewTemplate) ? 'animate-pulse' : ''}`}
                    />
                  </button>
                )}
                {(viewTemplate.syncStatus === 'DB_ONLY' ||
                  (viewTemplate.inDatabase !== false &&
                    (viewTemplate.inInterakt === false || viewTemplate.interaktPresent === false))) && (
                  <button
                    type="button"
                    onClick={() => {
                      setViewTemplate(null);
                      startEdit(viewTemplate);
                    }}
                    className={actionBtnClass}
                    title="Edit & submit to Interakt"
                    aria-label="Edit and submit to Interakt"
                  >
                    <CloudArrowUpIcon className="h-4 w-4 text-emerald-600" />
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => setConfirmEdit(viewTemplate)}
                  className={actionBtnClass}
                  title="Edit"
                  aria-label="Edit template"
                >
                  <Pencil className="h-4 w-4" />
                </button>
                {viewTemplate.inDatabase !== false && (
                  <button
                    type="button"
                    onClick={() => setConfirmDelete(viewTemplate)}
                    className={`${actionBtnClass} text-red-600 dark:text-red-400`}
                    title="Delete"
                    aria-label="Delete template"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
                <button type="button" onClick={() => setViewTemplate(null)} aria-label="Close">
                  <XMarkIcon className="h-5 w-5 text-gray-500" />
                </button>
              </div>
            </div>

            <div className="grid min-h-0 flex-1 overflow-hidden lg:grid-cols-[1fr_320px]">
              <div className="overflow-y-auto border-b border-gray-200 p-5 lg:border-b-0 lg:border-r dark:border-gray-700">
                <dl className="space-y-4 text-sm">
                  <div>
                    <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">Status</dt>
                    <dd className="mt-1">
                      <ApprovalBadge status={viewTemplate.approvalStatus} />
                    </dd>
                  </div>
                  <div>
                    <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">Category</dt>
                    <dd className="mt-1">{viewTemplate.category}</dd>
                  </div>
                  <div>
                    <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">Language</dt>
                    <dd className="mt-1">{viewTemplate.languageCode}</dd>
                  </div>
                  <div>
                    <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">Header type</dt>
                    <dd className="mt-1">{viewTemplate.headerFormat || 'NONE'}</dd>
                  </div>
                  {viewTemplate.headerText && (
                    <div>
                      <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">Header text</dt>
                      <dd className="mt-1 whitespace-pre-wrap">{viewTemplate.headerText}</dd>
                    </div>
                  )}
                  {viewTemplate.footer && (
                    <div>
                      <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">Footer</dt>
                      <dd className="mt-1">{viewTemplate.footer}</dd>
                    </div>
                  )}
                  <div>
                    <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">Variables</dt>
                    <dd className="mt-1">{viewTemplate.variableCount}</dd>
                  </div>
                  {viewTemplate.waTemplateId && (
                    <div>
                      <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">WhatsApp template ID</dt>
                      <dd className="mt-1 break-all font-mono text-xs">{viewTemplate.waTemplateId}</dd>
                    </div>
                  )}
                  {viewTemplate.interaktTemplateId && (
                    <div>
                      <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">Interakt ID</dt>
                      <dd className="mt-1 break-all font-mono text-xs">{viewTemplate.interaktTemplateId}</dd>
                    </div>
                  )}
                  <div>
                    <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">Body</dt>
                    <dd className="mt-1 whitespace-pre-wrap rounded-lg bg-gray-50 p-3 font-mono text-xs leading-relaxed dark:bg-gray-800">
                      {viewTemplate.bodyPreview || '—'}
                    </dd>
                  </div>
                  <div className="pt-2">
                    <WhatsAppTemplateMappingsSection
                      bodyPreview={viewTemplate.bodyPreview || ''}
                      mappings={viewMappings}
                      onMappingsChange={setViewMappings}
                      showSaveButton
                      saving={mappingSaving}
                      onSave={handleSaveViewMappings}
                    />
                  </div>
                  {(viewTemplate.buttons?.length ?? 0) > 0 && (
                    <div>
                      <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">Buttons</dt>
                      <dd className="mt-2 space-y-2">
                        {viewTemplate.buttons!.map((btn, i) => (
                          <div
                            key={`${btn.type}-${btn.text}-${i}`}
                            className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-800"
                          >
                            <p className="font-medium text-gray-900 dark:text-white">{btn.text}</p>
                            <p className="mt-0.5 text-xs text-gray-500">{templateButtonTypeLabel(btn.type)}</p>
                            {btn.url && (
                              <p className="mt-1 break-all text-xs text-[#009de2]">{btn.url}</p>
                            )}
                            {btn.phoneNumber && (
                              <p className="mt-1 text-xs text-gray-600">{btn.phoneNumber}</p>
                            )}
                          </div>
                        ))}
                      </dd>
                    </div>
                  )}
                  <div className="pt-2">
                    <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">Version history</dt>
                    <dd className="mt-2">
                      {loadingVersions ? (
                        <p className="text-xs text-gray-500">Loading version history...</p>
                      ) : versionHistory.length === 0 ? (
                        <p className="text-xs text-gray-500">No version history yet.</p>
                      ) : (
                        <ul className="max-h-48 space-y-2 overflow-y-auto">
                          {versionHistory.map((entry) => (
                            <li
                              key={entry.id}
                              className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-xs dark:border-gray-700 dark:bg-gray-800"
                            >
                              <div className="flex flex-wrap items-center gap-2">
                                <span className="font-medium">v{entry.version}</span>
                                <span className="rounded bg-gray-200 px-1.5 py-0.5 dark:bg-gray-700">
                                  {entry.changeType}
                                </span>
                                <span className="text-gray-500">
                                  {new Date(entry.createdAt).toLocaleString()}
                                </span>
                              </div>
                              {entry.changeNote && (
                                <p className="mt-1 text-gray-600 dark:text-gray-400">{entry.changeNote}</p>
                              )}
                            </li>
                          ))}
                        </ul>
                      )}
                    </dd>
                  </div>
                </dl>
              </div>

              <div className="flex shrink-0 flex-col items-center overflow-y-auto bg-gray-50/80 p-4 dark:bg-gray-950/40">
                {viewPreviewProps && (
                  <WhatsAppDevicePreview
                    message={viewPreviewProps.message}
                    headerText={viewPreviewProps.headerText}
                    footer={viewPreviewProps.footer}
                    buttons={viewPreviewProps.buttons}
                    headerMediaUrl={viewPreviewProps.headerMediaUrl}
                    headerMimeType={viewPreviewProps.headerMimeType}
                    contactName="Vedika.Health"
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
        {[
          { label: 'Total', value: overview.total, tone: 'bg-slate-50 text-slate-800 dark:bg-slate-900/40 dark:text-slate-200' },
          { label: 'Approved', value: overview.approved, tone: 'bg-green-50 text-green-800 dark:bg-green-950/30 dark:text-green-300' },
          { label: 'Pending', value: overview.pending, tone: 'bg-amber-50 text-amber-800 dark:bg-amber-950/30 dark:text-amber-300' },
          { label: 'Rejected', value: overview.rejected, tone: 'bg-red-50 text-red-800 dark:bg-red-950/30 dark:text-red-300' },
        ].map((card) => (
          <button
            key={card.label}
            type="button"
            onClick={() => {
              if (card.label === 'Total') setApprovalStatus('ALL');
              else setApprovalStatus(card.label.toUpperCase());
            }}
            className={`rounded-xl border border-transparent px-4 py-3 text-left ${card.tone}`}
          >
            <p className="text-xs font-medium opacity-80">{card.label}</p>
            <p className="mt-1 text-2xl font-semibold tabular-nums">{card.value}</p>
          </button>
        ))}
      </div>

      <div className="mb-4 grid gap-3 rounded-xl border border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-gray-900 sm:grid-cols-2 lg:grid-cols-6">
        <div className="relative sm:col-span-2 lg:col-span-2">
          <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search name, body, category..."
            className="w-full rounded-lg border border-gray-200 py-2 pl-9 pr-3 text-sm dark:border-gray-600 dark:bg-gray-950"
          />
        </div>
        <select
          value={approvalStatus}
          onChange={(e) => setApprovalStatus(e.target.value)}
          className="rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-950"
        >
          <option value="ALL">All statuses</option>
          <option value="APPROVED">Approved</option>
          <option value="PENDING">Pending</option>
          <option value="REJECTED">Rejected</option>
          <option value="DRAFT">Draft</option>
        </select>
        <select
          value={inDatabaseFilter}
          onChange={(e) => setInDatabaseFilter(e.target.value)}
          className="rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-950"
        >
          <option value="ALL">Saved in DB: all</option>
          <option value="YES">Saved in DB</option>
          <option value="NO">Not saved in DB</option>
        </select>
        <select
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          className="rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-950"
        >
          <option value="ALL">All categories</option>
          <option value="UTILITY">Utility</option>
          <option value="MARKETING">Marketing</option>
          <option value="AUTHENTICATION">Authentication</option>
        </select>
        <select
          value={headerFormat}
          onChange={(e) => setHeaderFormat(e.target.value)}
          className="rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-950"
        >
          <option value="ALL">All headers</option>
          <option value="NONE">None</option>
          <option value="TEXT">Text</option>
          <option value="IMAGE">Image</option>
          <option value="VIDEO">Video</option>
          <option value="DOCUMENT">Document</option>
        </select>
      </div>

      <div className="mb-3 flex flex-wrap items-center justify-between gap-2 text-xs text-gray-500">
        <p>
          Showing <span className="font-medium text-gray-700 dark:text-gray-300">{templates.length}</span> of{' '}
          <span className="font-medium text-gray-700 dark:text-gray-300">{filteredTotal}</span> matching
          {overview.draft > 0 ? ` · ${overview.draft} draft` : ''}
          {overview.interaktOnly > 0 ? ` · ${overview.interaktOnly} Interakt-only` : ''}
        </p>
        {(search ||
          approvalStatus !== 'ALL' ||
          inDatabaseFilter !== 'ALL' ||
          category !== 'ALL' ||
          headerFormat !== 'ALL') && (
          <button
            type="button"
            onClick={() => {
              setSearchInput('');
              setSearch('');
              setApprovalStatus('ALL');
              setInDatabaseFilter('ALL');
              setCategory('ALL');
              setLanguageCode('all');
              setHeaderFormat('ALL');
            }}
            className="text-[#009de2] hover:underline"
          >
            Clear filters
          </button>
        )}
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <ArrowPathIcon className="h-4 w-4 animate-spin" /> Loading templates...
        </div>
      ) : templates.length === 0 ? (
        <p className="text-sm text-gray-500">No templates match your filters.</p>
      ) : (
        <>
          <div className="space-y-3 md:hidden">
            {templates.map((t, index) => (
              <div key={`${t.id}-${t.name}`} className="rounded-xl border border-gray-200 p-4 dark:border-gray-700">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-xs text-gray-400">#{index + 1}</span>
                      <p className="font-medium text-gray-900 dark:text-white">{t.displayName || t.name}</p>
                      <ApprovalBadge status={t.approvalStatus} />
                      <SyncStatusBadge inDatabase={t.inDatabase} status={t.syncStatus} />
                    </div>
                    <p className="mt-1 truncate font-mono text-xs text-gray-500">{t.name}</p>
                  </div>
                  {renderActionButtons(t)}
                </div>
                <div className="mt-2 flex flex-wrap gap-2 text-xs">
                  <span className="rounded-full bg-gray-100 px-2 py-0.5 dark:bg-gray-800">{t.category}</span>
                  <span className="text-gray-500">{t.languageCode}</span>
                  <span className="text-gray-500">{t.variableCount} vars</span>
                </div>
                {t.bodyPreview && (
                  <p className="mt-2 line-clamp-2 text-xs text-gray-600 dark:text-gray-400">{t.bodyPreview}</p>
                )}
              </div>
            ))}
          </div>

          <div className="hidden overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-700 md:block">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-800/80">
                <tr>
                  <th className="px-3 py-2 text-left font-medium">Sr</th>
                  <th className="px-4 py-2 text-left font-medium">Name</th>
                  <th className="px-4 py-2 text-left font-medium">Display name</th>
                  <th className="px-4 py-2 text-left font-medium">Status</th>
                  <th className="px-4 py-2 text-left font-medium">Saved in DB</th>
                  <th className="px-4 py-2 text-left font-medium">Category</th>
                  <th className="px-4 py-2 text-left font-medium">Lang</th>
                  <th className="px-4 py-2 text-left font-medium">Header</th>
                  <th className="px-4 py-2 text-left font-medium">Variables</th>
                  <th className="px-4 py-2 text-left font-medium">Preview</th>
                  <th className="px-4 py-2 text-right font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {templates.map((t, index) => (
                  <tr key={`${t.id}-${t.name}`} className="border-t border-gray-100 dark:border-gray-800">
                    <td className="px-3 py-2 text-xs text-gray-400">{index + 1}</td>
                    <td className="px-4 py-2 font-mono text-xs">{t.name}</td>
                    <td className="px-4 py-2">{t.displayName || '—'}</td>
                    <td className="px-4 py-2">
                      <ApprovalBadge status={t.approvalStatus} />
                    </td>
                    <td className="px-4 py-2">
                      <SyncStatusBadge inDatabase={t.inDatabase} status={t.syncStatus} />
                    </td>
                    <td className="px-4 py-2">{t.category}</td>
                    <td className="px-4 py-2">{t.languageCode}</td>
                    <td className="px-4 py-2">{t.headerFormat || '—'}</td>
                    <td className="px-4 py-2">{t.variableCount}</td>
                    <td className="max-w-xs truncate px-4 py-2 text-gray-500">{t.bodyPreview || '—'}</td>
                    <td className="px-4 py-2">{renderActionButtons(t)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {(loadingMore || hasMore) && (
            <div
              ref={loadMoreRef}
              className="mt-3 flex items-center justify-center gap-2 py-4 text-sm text-gray-500"
            >
              {loadingMore ? (
                <>
                  <ArrowPathIcon className="h-4 w-4 animate-spin" />
                  Loading more...
                </>
              ) : (
                <span className="text-xs text-gray-400">Scroll to load more</span>
              )}
            </div>
          )}
        </>
      )}

      <ConfirmDialog
        open={!!confirmEdit}
        title="Edit template?"
        message={
          confirmEdit
            ? `Edit "${confirmEdit.displayName || confirmEdit.name}"? You can change the code name and content, save in Vedika DB, and optionally submit to Interakt.`
            : ''
        }
        tone="warning"
        confirmLabel="Continue to edit"
        cancelLabel="Cancel"
        stackLevel="top"
        onCancel={() => setConfirmEdit(null)}
        onConfirm={() => {
          if (confirmEdit) startEdit(confirmEdit);
        }}
      />

      <ConfirmDialog
        open={!!confirmDelete}
        title="Delete from database?"
        message={
          confirmDelete
            ? `Remove "${confirmDelete.displayName || confirmDelete.name}" from the Vedika database only? ` +
              'It will NOT be deleted from Interakt. A deletion log will be saved so you can restore it later.'
            : ''
        }
        tone="danger"
        confirmLabel="Remove from database"
        cancelLabel="Keep template"
        busy={deleting}
        stackLevel="top"
        onCancel={() => {
          if (!deleting) setConfirmDelete(null);
        }}
        onConfirm={() => void handleDeleteConfirm()}
      />
    </WhatsAppPanelShell>
  );
};

export default WhatsAppTemplatesPanel;
